import React, { useState, useEffect, useCallback } from 'react';
import Ghost from './Ghost.jsx';
import './auth-modal.css';
import { useAuthStore } from '../../store/authStore';
import { AUTH_BASE } from '../../services/oidcService';
import { isTauri } from '../../utils/tauri';
import { isWebApp } from '../../platform/target';
import { beginWebLogin } from '../../services/webOidc';

/* ─── icons ─────────────────────────────────────────────────────────────── */
const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6 18 18M18 6 6 18" /></svg>,
  warn: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7.5v5" /><circle cx="12" cy="16" r=".6" fill="currentColor" stroke="none" /></svg>,
  browser: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M3.5 9h17M3.5 15h17" /><path d="M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" /></svg>,
};

async function openExternal(url) {
  if (isTauri()) {
    const { open } = await import('@tauri-apps/plugin-shell');
    await open(url);
  } else {
    window.open(url, '_blank', 'noopener');
  }
}

/**
 * Sign-in modal. Auth lives entirely at the central IdP (auth.apiboo.com):
 * the button starts the OIDC browser flow (Authorization Code + PKCE via the
 * system browser and the Rust loopback listener) and this modal just tracks
 * its progress. The hosted pages carry the full login UI — email/password,
 * registration, password reset, 2FA and passkeys — in the same design as
 * this modal.
 */
const AuthModal = ({
  isOpen,
  onClose,
  initialMode = 'login',
  resetLink = null,
  onResetComplete,
  scrimBlur = 1.5,
}) => {
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState(null);
  const webApp = isWebApp();

  const loginWithBrowser = useAuthStore((s) => s.loginWithBrowser);
  const cancelLoginWithBrowser = useAuthStore((s) => s.cancelLoginWithBrowser);

  const themeStyle = { '--scrim-blur': scrimBlur + 'px' };

  // Legacy deep links / command palette still ask for specific auth pages —
  // they now live at the IdP, so just open the right one in the browser.
  useEffect(() => {
    if (!isOpen) return;
    if (resetLink || initialMode === 'forgot' || initialMode === 'reset') {
      openExternal(`${AUTH_BASE}/forgot-password`).catch(() => {});
      onResetComplete?.();
    } else if (initialMode === 'register') {
      openExternal(`${AUTH_BASE}/register`).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setWaiting(false);
      setError(null);
    }
  }, [isOpen]);

  const handleSignIn = useCallback(async () => {
    setError(null);
    setWaiting(true);
    const result = await loginWithBrowser();
    setWaiting(false);
    if (result.success) {
      onClose?.();
    } else if (!result.cancelled) {
      setError(result.error || 'Sign-in failed. Please try again.');
    }
  }, [loginWithBrowser, onClose]);

  // Web app: leave for the IdP in this tab. It only returns here on failure to
  // even start (e.g. no WebCrypto), which is shown like any other error.
  const handleWebSignIn = useCallback(async () => {
    setError(null);
    setWaiting(true);
    try {
      await beginWebLogin();
    } catch (e) {
      setWaiting(false);
      setError(e?.message || 'Sign-in could not be started. Please try again.');
    }
  }, []);

  const handleCancel = useCallback(async () => {
    await cancelLoginWithBrowser();
    setWaiting(false);
  }, [cancelLoginWithBrowser]);

  const handleClose = useCallback(() => {
    if (waiting) cancelLoginWithBrowser();
    onClose?.();
  }, [waiting, cancelLoginWithBrowser, onClose]);

  if (!isOpen) return null;

  return (
    <div className="apiboo-auth-scrim" style={themeStyle}>
      <div
        className="modal modal-anim"
        style={{ '--modal-w': '380px' }}
        role="dialog"
        aria-modal="true"
        aria-label="Sign in to Apiboo"
      >
        <button className="m-close" aria-label="Close" onClick={handleClose}>{I.close}</button>

        <div className="m-head">
          <div className="m-logo">
            <Ghost variant="classic" size={44} animated glow />
          </div>
          <div className="m-ttl">{waiting && !webApp ? 'Continue in your browser' : 'Welcome to Apiboo'}</div>
        </div>

        <div className="m-body">
          {error && (
            <div className="err-msg" style={{ marginTop: 0, marginBottom: 14 }}>
              {I.warn}{error}
            </div>
          )}

          {/* Web app: the desktop flow needs the Rust loopback listener, which a
              browser does not have. The browser version redirects this tab to
              auth.apiboo.com and comes back to /auth/callback (services/webOidc). */}
          {webApp && (
            <div className="view" key="web" data-testid="auth-web-sign-in">
              <p style={{ margin: '0 0 18px', fontSize: 13.5, color: 'var(--fg-2)', lineHeight: 1.55, textAlign: 'center' }}>
                Sign in or create your account securely at auth.apiboo.com — passkeys,
                two-factor and GitHub sign-in included. You come straight back here.
              </p>
              <button className="btn btn-primary" onClick={handleWebSignIn} disabled={waiting} style={{ width: '100%' }}>
                {waiting ? 'Opening sign-in…' : 'Continue to sign in'}
              </button>
            </div>
          )}

          {!webApp && !waiting && (
            <div className="view" key="start">
              <p style={{ margin: '0 0 18px', fontSize: 13.5, color: 'var(--fg-2)', lineHeight: 1.55, textAlign: 'center' }}>
                Sign in or create your account securely in the browser — passkeys,
                two-factor and GitHub sign-in included.
              </p>
              <button className="btn btn-primary" onClick={handleSignIn} style={{ width: '100%' }}>
                <span style={{ display: 'inline-flex', width: 16, height: 16, marginRight: 8, verticalAlign: '-3px' }}>{I.browser}</span>
                Continue in browser
              </button>
            </div>
          )}

          {!webApp && waiting && (
            <div className="view" key="waiting">
              <p style={{ margin: '0 0 18px', fontSize: 13.5, color: 'var(--fg-2)', lineHeight: 1.55, textAlign: 'center' }}>
                We opened your browser to finish signing in. Come back here once
                you're done — this window updates automatically.
              </p>
              <button className="btn btn-ghost" onClick={handleCancel} style={{ width: '100%' }}>
                Cancel
              </button>
            </div>
          )}

          <div style={{ marginTop: 16, textAlign: 'center', fontSize: 11.5, color: 'var(--fg-3)', lineHeight: 1.6 }}>
            By continuing you agree to our{' '}
            <button className="lk" onClick={() => openExternal('https://apiboo.com/terms')}>Terms of Service</button>
            {' '}and{' '}
            <button className="lk" onClick={() => openExternal('https://apiboo.com/privacy')}>Privacy Policy</button>.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
